import type { WaiterReservation } from './waiterApi';

/** Fallback when an older server omits `grace_minutes` — hub's own value. */
export const DEFAULT_RESERVATION_GRACE_MINUTES = 30;

/** « dans X min » is only worth a badge this close to the booking. */
const SOON_WINDOW_MINUTES = 60;

const MINUTE_MS = 60_000;

/**
 * SC-966 — the rows the « Résas » tab shows: still-confirmed bookings whose
 * time plus the grace window is not behind us yet, nearest first.
 *
 * hub already filters with the same rule, but its answer is only as fresh as
 * the last poll (up to 5 minutes once nothing changes). Re-filtering against a
 * ticking clock is what makes a booking leave the list on time. The sort is
 * stable, so equal times keep hub's `created_at` order.
 */
export function visibleReservations(
  reservations: WaiterReservation[],
  nowMs: number,
  graceMinutes: number = DEFAULT_RESERVATION_GRACE_MINUTES,
): WaiterReservation[] {
  const graceMs = graceMinutes * MINUTE_MS;
  return reservations
    .filter((r) => r.status === 'CONFIRMED' && Date.parse(r.reservedFor) + graceMs >= nowMs)
    .sort((a, b) => Date.parse(a.reservedFor) - Date.parse(b.reservedFor));
}

/**
 * SC-970 — the five buckets of the « Résas » filter bar.
 *
 * `upcoming` is the pre-SC-970 screen, kept as the default: still-confirmed
 * bookings whose grace window has not run out. The other four are plain status
 * buckets over the whole day — a booking marked by mistake has to stay
 * reachable, and `all` is where a forgotten 12:00 booking is found at 20:00.
 */
export type ReservationFilterKey = 'all' | 'upcoming' | 'arrived' | 'noShow' | 'cancelled';

export const RESERVATION_FILTER_KEYS: ReservationFilterKey[] = [
  'all',
  'upcoming',
  'arrived',
  'noShow',
  'cancelled',
];

export const DEFAULT_RESERVATION_FILTER: ReservationFilterKey = 'upcoming';

const STATUS_OF_FILTER: Partial<Record<ReservationFilterKey, string>> = {
  arrived: 'COMPLETED',
  noShow: 'NO_SHOW',
  cancelled: 'CANCELLED',
};

/** The day's rows a bucket shows, nearest first. */
export function filterReservations(
  reservations: WaiterReservation[],
  filter: ReservationFilterKey,
  nowMs: number,
  graceMinutes: number = DEFAULT_RESERVATION_GRACE_MINUTES,
): WaiterReservation[] {
  if (filter === 'upcoming') return visibleReservations(reservations, nowMs, graceMinutes);
  const wanted = STATUS_OF_FILTER[filter];
  return reservations
    .filter((r) => wanted === undefined || r.status === wanted)
    .sort((a, b) => Date.parse(a.reservedFor) - Date.parse(b.reservedFor));
}

/** What each chip has to display next to its label. */
export function reservationFilterCounts(
  reservations: WaiterReservation[],
  nowMs: number,
  graceMinutes: number = DEFAULT_RESERVATION_GRACE_MINUTES,
): Record<ReservationFilterKey, number> {
  return {
    all: reservations.length,
    upcoming: visibleReservations(reservations, nowMs, graceMinutes).length,
    arrived: reservations.filter((r) => r.status === 'COMPLETED').length,
    noShow: reservations.filter((r) => r.status === 'NO_SHOW').length,
    cancelled: reservations.filter((r) => r.status === 'CANCELLED').length,
  };
}

/**
 * SC-970 — where « revenir en arrière » lands, and hub agrees: back to
 * CANCELLED when the booking carries a cancellation, back to CONFIRMED when it
 * does not. Correcting an arrival/no-show never erases a cancellation, so
 * `cancelledAt` alone answers the question — no extra column, no guesswork.
 */
export function reservationUndoTarget(
  reservation: WaiterReservation,
): 'CONFIRMED' | 'CANCELLED' {
  return reservation.cancelledAt ? 'CANCELLED' : 'CONFIRMED';
}

export type ReservationTiming =
  | { kind: 'late'; minutes: number }
  | { kind: 'now' }
  | { kind: 'soon'; minutes: number }
  | { kind: 'later' };

/**
 * Where a booking sits relative to now, in whole minutes. `late` starts at
 * the first full minute past the time; the minute either side of it reads
 * « maintenant ».
 */
export function reservationTiming(reservedForIso: string, nowMs: number): ReservationTiming {
  const deltaMs = Date.parse(reservedForIso) - nowMs;
  if (deltaMs <= -MINUTE_MS) return { kind: 'late', minutes: Math.floor(-deltaMs / MINUTE_MS) };
  if (deltaMs < MINUTE_MS) return { kind: 'now' };
  const minutes = Math.ceil(deltaMs / MINUTE_MS);
  return minutes <= SOON_WINDOW_MINUTES ? { kind: 'soon', minutes } : { kind: 'later' };
}

/** « Pas venu » only makes sense once the booked time has come (hub agrees). */
export function canMarkNoShow(reservedForIso: string, nowMs: number): boolean {
  return Date.parse(reservedForIso) <= nowMs;
}

export function totalCovers(reservations: WaiterReservation[]): number {
  return reservations.reduce((sum, r) => sum + r.partySize, 0);
}

/**
 * SC-1612 — the carnet's ‹ › day arrows. Pure calendar arithmetic on a
 * `YYYY-MM-DD` string (UTC noon, so no daylight-saving day is skipped or
 * doubled), whatever the phone's time zone.
 */
export function shiftDay(day: string, days: number): string {
  const at = new Date(`${day}T12:00:00Z`);
  at.setUTCDate(at.getUTCDate() + days);
  return at.toISOString().slice(0, 10);
}

/**
 * SC-1612 — the 42 days (six Monday-first weeks) the carnet's calendar shows
 * for `month` (`YYYY-MM`), first cell on or before the 1st.
 */
export function calendarGrid(month: string): string[] {
  const first = `${month}-01`;
  const weekday = (new Date(`${first}T12:00:00Z`).getUTCDay() + 6) % 7; // lundi = 0
  const start = shiftDay(first, -weekday);
  return Array.from({ length: 42 }, (_, i) => shiftDay(start, i));
}
